import {
    asyncListWithSkipTakeCreate,
    cachedValueCreateWithParamsV2,
    Evaluable,
    evaluateWhenFunction,
    hashFunctionCreate,
    stringReplaceEmptyOrWhiteSpaceValue,
    valueConvertWhenNotNullable
} from '@wix/devzai-utils-common';
import type {DoppeDtoEvent, DoppeDtoEventRegistrationType} from '../../../client-server-common/types/doppe-dto-event';
import type {IDoppeViewerApi} from '../../../client-server-common/api/doppe-viewer-api';

export function doppeViewerAsyncListFactoryEvents(
    viewerApiClientGetter: Evaluable<() => IDoppeViewerApi>
) {
    return cachedValueCreateWithParamsV2({
        paramsHashFunction: hashFunctionCreate<doppeViewerAsyncListFactoryEvents.Params>({
            search: hashFunctionCreate(),
            registrationType: hashFunctionCreate(),
            pastEventsOnly: hashFunctionCreate(),
        }),
        paramsNormalizationFunction: params => ({
            ...params,
            search: valueConvertWhenNotNullable(params.search, s =>
                stringReplaceEmptyOrWhiteSpaceValue(s, undefined)
            )
        }),
        valueFunction: (params) => {
            const viewerApiClient = evaluateWhenFunction(viewerApiClientGetter);

            return asyncListWithSkipTakeCreate<DoppeDtoEvent>({
                fetchFunction: async (skip, take) => {
                    return viewerApiClient.getEvents({
                        skip: skip,
                        take: take,
                        search: params.search,
                        registrationType: params.registrationType,
                        pastEventsOnly: params.pastEventsOnly,
                    });
                },
                defaultTake: 30,
            });
        }
    });
}

export namespace doppeViewerAsyncListFactoryEvents {
    export interface Params {
        search?: string;
        registrationType?: DoppeDtoEventRegistrationType;
        pastEventsOnly?: boolean;
    }
}

export function doppeViewerAsyncListFactoryEventsByCategory(
    viewerApiClientGetter: Evaluable<() => IDoppeViewerApi>,
    resolveCategoryIdByName: (name: string) => Promise<string | null>,
) {
    return cachedValueCreateWithParamsV2({
        paramsHashFunction: hashFunctionCreate<doppeViewerAsyncListFactoryEventsByCategory.Params>({
            categorySlug: hashFunctionCreate(),
        }),
        valueFunction: (params) => {
            const viewerApiClient = evaluateWhenFunction(viewerApiClientGetter);

            let resolvedCategoryId: string | null | undefined = undefined;

            return asyncListWithSkipTakeCreate<DoppeDtoEvent>({
                fetchFunction: async (skip, take) => {
                    if (resolvedCategoryId === undefined) {
                        resolvedCategoryId = await resolveCategoryIdByName(params.categorySlug);
                    }

                    if (!resolvedCategoryId) {
                        return {results: [], count: 0};
                    }

                    return viewerApiClient.getEventsByCategory({
                        skip: skip,
                        take: take,
                        categoryId: resolvedCategoryId,
                    });
                },
                defaultTake: 30,
            });
        }
    });
}

export namespace doppeViewerAsyncListFactoryEventsByCategory {
    export interface Params {
        categorySlug: string;
    }
}
